import { distinctIdentityHandle } from "@/src/lib/identityPresentation";
import { contentMaxWidths } from "@/src/theme/layout";
import { Alert } from "@/src/lib/alert";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { useProfile } from "@/src/hooks/useProfile";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { auth } from "@/src/lib/firebase";
import { accountDeletionErrorMessage, deleteCurrentAccount } from "@/src/lib/accountDeletion";
import { getAccountType, getVerificationStatus } from "@/src/lib/identity";
import { verificationStatusLabel } from "@/src/lib/accountPresentation";
import { cancelIdentityRequest, createIdentityRequest, listMyIdentityRequests } from "@/src/lib/identityRequests";
import type { IdentityRequest, IdentityRequestKind } from "@/src/types/identity";
import { AccountSwitcher } from "@/src/components/AccountSwitcher";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { Ionicons } from "@/src/components/ui/ionicons";
import { router } from "expo-router";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signOut,
  updatePassword,
} from "firebase/auth";
import React, { useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;


type IconName = React.ComponentProps<typeof Ionicons>["name"];

export default function SettingsScreen() {
  const user = auth.currentUser;
  const { profile } = useProfile(user);
  const { activeAccount, personalAccount } = useActiveAccount();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [securityExpanded, setSecurityExpanded] = useState(false);
  const [identityRequests, setIdentityRequests] = useState<IdentityRequest[]>([]);
  const [loadingIdentity, setLoadingIdentity] = useState(true);
  const [identityError, setIdentityError] = useState(false);
  const [identityAttempt, setIdentityAttempt] = useState(0);
  const [identityBusy, setIdentityBusy] = useState<IdentityRequestKind | null>(null);
  const [identityNote, setIdentityNote] = useState("");
  const [declaredInstitution, setDeclaredInstitution] = useState("");
  const [identityDetailsOpen, setIdentityDetailsOpen] = useState(false);
  const [deletionExpanded, setDeletionExpanded] = useState(false);
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);
  const [deletionPassword, setDeletionPassword] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deletionError, setDeletionError] = useState("");

  const username = useMemo(() => String(profile?.username ?? "").trim(), [profile?.username]);
  const personalName = String(profile?.displayName ?? personalAccount?.displayName ?? "").trim();
  const activeIdentity = activeAccount ?? personalAccount ?? profile;
  const accountType = getAccountType(activeIdentity);
  const verificationStatus = getVerificationStatus(activeIdentity);

  React.useEffect(() => {
    if (!user?.uid) {
      setIdentityRequests([]);
      setLoadingIdentity(false);
      return;
    }

    let active = true;
    setLoadingIdentity(true);
    setIdentityError(false);
    listMyIdentityRequests()
      .then((requests) => active && setIdentityRequests(requests))
      .catch(() => active && setIdentityError(true))
      .finally(() => active && setLoadingIdentity(false));
    return () => { active = false; };
  }, [identityAttempt, user?.uid]);

  const latestRequest = useMemo(() => {
    const latest = new Map<IdentityRequestKind, IdentityRequest>();
    for (const request of identityRequests) {
      if (!latest.has(request.kind)) latest.set(request.kind, request);
    }
    return latest;
  }, [identityRequests]);

  async function submitIdentityRequest(kind: IdentityRequestKind) {
    if (identityBusy || !user?.uid) return;
    setIdentityBusy(kind);
    try {
      await createIdentityRequest({
        kind,
        ...(identityNote.trim() ? { note: identityNote.trim() } : {}),
        ...(kind === "institution_identity" && declaredInstitution.trim()
          ? { declaredInstitution: declaredInstitution.trim() }
          : {}),
      });
      setIdentityNote("");
      setDeclaredInstitution("");
      setIdentityDetailsOpen(false);
      setIdentityAttempt((value) => value + 1);
      Alert.alert("Demande envoyée", "Ta demande a été envoyée.");
    } catch (error: any) {
      Alert.alert("Demande impossible", friendlyFirebaseError(error, "La demande n’a pas pu être envoyée."));
    } finally {
      setIdentityBusy(null);
    }
  }

  function cancelRequest(request: IdentityRequest) {
    if (identityBusy) return;
    Alert.alert("Annuler cette demande ?", "Tu pourras en déposer une nouvelle plus tard.", [
      { text: "Garder", style: "cancel" },
      {
        text: "Annuler",
        style: "destructive",
        onPress: () => void (async () => {
          setIdentityBusy(request.kind);
          try {
            await cancelIdentityRequest(request.id);
            setIdentityAttempt((value) => value + 1);
          } catch (error: any) {
            Alert.alert("Annulation impossible", friendlyFirebaseError(error, "La demande n’a pas pu être annulée."));
          } finally {
            setIdentityBusy(null);
          }
        })(),
      },
    ]);
  }

  async function onChangePassword() {
    if (!user?.email) {
      Alert.alert("Erreur", "Aucun email n’est associé à ce compte.");
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      Alert.alert("Erreur", "Remplis tous les champs.");
      return;
    }

    if (newPassword.length < 6) {
      Alert.alert("Erreur", "Le nouveau mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert("Erreur", "Les deux mots de passe ne correspondent pas.");
      return;
    }

    try {
      setSavingPassword(true);
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      Alert.alert("Mot de passe modifié", "Ton mot de passe a été mis à jour.");
    } catch (error: any) {
      Alert.alert(
        "Impossible de changer le mot de passe",
        friendlyFirebaseError(error, "Vérifie ton mot de passe actuel et réessaie.")
      );
    } finally {
      setSavingPassword(false);
    }
  }

  async function onSendResetEmail() {
    if (!user?.email) {
      Alert.alert("Erreur", "Aucun email n’est associé à ce compte.");
      return;
    }

    try {
      setSendingReset(true);
      await sendPasswordResetEmail(auth, user.email);
      Alert.alert("Email envoyé", "Un lien de réinitialisation a été envoyé.");
    } catch (error: any) {
      Alert.alert("Erreur", friendlyFirebaseError(error, "Impossible d’envoyer l’email."));
    } finally {
      setSendingReset(false);
    }
  }

  async function onLogout() {
    try {
      await signOut(auth);
      router.replace("/(auth)/login");
    } catch (error: any) {
      Alert.alert("Erreur", friendlyFirebaseError(error, "Déconnexion impossible."));
    }
  }

  async function onDeleteAccount() {
    if (deletingAccount) return;
    setDeletingAccount(true);
    setDeletionError("");
    try {
      await deleteCurrentAccount(deletionPassword);
      router.replace("/(auth)/login");
    } catch (error: any) {
      setDeletionError(accountDeletionErrorMessage(
        error,
        friendlyFirebaseError(error, "La suppression du compte a échoué. Réessaie.")
      ));
      setConfirmingDeletion(false);
    } finally {
      setDeletingAccount(false);
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <ScrollView
        contentContainerStyle={{
          alignSelf: "center",
          maxWidth: contentMaxWidths.settings,
          width: "100%",
          paddingHorizontal: 16,
          paddingTop: 12,
          paddingBottom: 40,
          gap: 12,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 2 }}>
          <Pressable
            onPress={() => router.back()}
            accessibilityRole="button"
            accessibilityLabel="Retour"
            style={iconButtonStyle}
          >
            <Ionicons name="chevron-back" size={20} color={uiText.primary} />
          </Pressable>
          <Text style={{ color: uiText.primary, fontSize: 28, fontWeight: "900" }}>Paramètres</Text>
        </View>

        <SettingsSection title="Compte">
          <SettingsInfoRow icon="mail-outline" label="Email" value={user?.email || "—"} />
          <SettingsInfoRow
            icon="person-outline"
            label="Profil"
            value={personalName || (username ? `@${username}` : "Profil non renseigné")}
            detail={distinctIdentityHandle(personalName || username, username) ? `@${username}` : undefined}
          />
        </SettingsSection>

        <AccountSwitcher expanded label="Identité active" />

        <IdentityRequestCard
          accountType={accountType}
          verificationStatus={verificationStatus}
          loading={loadingIdentity}
          error={identityError}
          latestRequest={latestRequest}
          busy={identityBusy}
          note={identityNote}
          declaredInstitution={declaredInstitution}
          detailsOpen={identityDetailsOpen}
          onChangeNote={setIdentityNote}
          onChangeInstitution={setDeclaredInstitution}
          onToggleDetails={() => setIdentityDetailsOpen((value) => !value)}
          onSubmit={(kind) => void submitIdentityRequest(kind)}
          onCancel={cancelRequest}
          onRetry={() => setIdentityAttempt((value) => value + 1)}
        />

        <SettingsSection title="Notifications">
          <SettingsRow
            icon="notifications-outline"
            label="Notifications"
            value="Ouvrir"
            onPress={() => router.push("/(tabs)/notifications")}
          />
        </SettingsSection>

        <SettingsSection title="Sécurité">
          <SettingsRow
            icon="lock-closed-outline"
            label="Mot de passe"
            value={securityExpanded ? "Fermer" : "Modifier"}
            onPress={() => setSecurityExpanded((value) => !value)}
          />
          {securityExpanded ? (
            <PasswordForm
              currentPassword={currentPassword}
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              savingPassword={savingPassword}
              sendingReset={sendingReset}
              onChangeCurrent={setCurrentPassword}
              onChangeNew={setNewPassword}
              onChangeConfirm={setConfirmPassword}
              onChangePassword={onChangePassword}
              onSendResetEmail={onSendResetEmail}
            />
          ) : null}
          <SettingsRow
            icon="trash-outline"
            label="Supprimer mon compte"
            value={deletionExpanded ? "Fermer" : "Irréversible"}
            onPress={() => {
              setDeletionExpanded((value) => !value);
              setConfirmingDeletion(false);
              setDeletionError("");
            }}
          />
          {deletionExpanded ? (
            <View style={{ paddingVertical: 14, borderTopWidth: 1, borderTopColor: BORDER, gap: 10 }}>
              <Text style={{ color: MUTED, lineHeight: 20 }}>
                Cette action supprime ton compte Firebase et ton identité Personnelle. Tes publications et commentaires personnels seront supprimés. Les messages déjà remis à d’autres personnes peuvent rester dans leur historique, avec ton nom remplacé par « Compte supprimé ». Le transfert d’une Institution ou d’un groupe dont tu es le seul responsable bloque la suppression.
              </Text>
              <BrandTextInput
                value={deletionPassword}
                onChangeText={setDeletionPassword}
                placeholder="Mot de passe actuel"
                placeholderTextColor={uiText.muted}
                secureTextEntry
                editable={!deletingAccount}
                accessibilityLabel="Mot de passe actuel pour supprimer le compte"
                style={requestInputStyle}
              />
              {deletionError ? (
                <Text accessibilityRole="alert" style={{ color: uiText.danger, lineHeight: 19 }}>
                  {deletionError}
                </Text>
              ) : null}
              {confirmingDeletion ? (
                <>
                  <Text style={{ color: uiText.danger, fontWeight: "800" }}>
                    Confirme la suppression définitive. Cette action ne peut pas être annulée.
                  </Text>
                  <View style={{ flexDirection: "row", gap: 10 }}>
                    <SmallAction
                      label="Annuler"
                      busy={false}
                      onPress={() => setConfirmingDeletion(false)}
                    />
                    <SmallAction
                      label="Supprimer définitivement"
                      busy={deletingAccount}
                      danger
                      onPress={() => void onDeleteAccount()}
                    />
                  </View>
                </>
              ) : (
                <SmallAction
                  label="Continuer vers la suppression"
                  busy={deletingAccount}
                  danger
                  onPress={() => {
                    setDeletionError("");
                    setConfirmingDeletion(true);
                  }}
                />
              )}
            </View>
          ) : null}
        </SettingsSection>

        <SettingsSection title="Déconnexion">
          <Pressable
            onPress={onLogout}
            accessibilityRole="button"
            style={{
              minHeight: 48,
              justifyContent: "center",
              alignItems: "center",
              marginVertical: 12,
              borderRadius: 12,
              backgroundColor: surface.danger,
              borderWidth: 1,
              borderColor: border.danger,
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Se déconnecter</Text>
          </Pressable>
        </SettingsSection>
      </ScrollView>
    </SafeAreaView>
  );
}

const iconButtonStyle = {
  width: 40,
  height: 40,
  borderRadius: 12,
  backgroundColor: CARD,
  borderWidth: 1,
  borderColor: BORDER,
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View
      style={{
        backgroundColor: CARD,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: BORDER,
        paddingHorizontal: 14,
      }}
    >
      <Text style={{ color: uiText.primary, fontSize: 17, fontWeight: "900", paddingTop: 14 }}>{title}</Text>
      {children}
    </View>
  );
}

function SettingsInfoRow({
  icon,
  label,
  value,
  detail,
}: {
  icon: IconName;
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <View style={settingsRowStyle}>
      <Ionicons name={icon} size={19} color={uiText.secondary} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ color: MUTED, fontSize: 12, fontWeight: "800" }}>{label}</Text>
        <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: uiText.primary, marginTop: 2, fontWeight: "800" }}>
          {value}
        </Text>
        {detail ? <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: MUTED, marginTop: 1, fontSize: 12 }}>{detail}</Text> : null}
      </View>
    </View>
  );
}

function SettingsRow({
  icon,
  label,
  value,
  onPress,
}: {
  icon: IconName;
  label: string;
  value: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={({ pressed }) => ({ ...settingsRowStyle, opacity: pressed ? 0.72 : 1 })}
    >
      <Ionicons name={icon} size={19} color={uiText.secondary} />
      <Text style={{ flex: 1, minWidth: 0, color: uiText.primary, fontWeight: "800" }}>{label}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: uiText.brand, fontWeight: "800", maxWidth: 120 }}>{value}</Text>
      <Ionicons name="chevron-forward" size={17} color={MUTED} />
    </Pressable>
  );
}

const settingsRowStyle = {
  minHeight: 56,
  flexDirection: "row" as const,
  alignItems: "center" as const,
  gap: 12,
  borderTopWidth: 1,
  borderTopColor: BORDER,
};

function statusColors(status: "unverified" | "pending" | "verified" | "rejected") {
  if (status === "verified") return { color: uiText.primary, backgroundColor: surface.success, borderColor: brand.success };
  if (status === "pending") return { color: uiText.brand, backgroundColor: surface.selected, borderColor: brand.primary };
  if (status === "rejected") return { color: uiText.danger, backgroundColor: surface.danger, borderColor: border.danger };
  return { color: MUTED, backgroundColor: surface.overlay, borderColor: BORDER };
}

function StatusPill({ status }: { status: "unverified" | "pending" | "verified" | "rejected" }) {
  const colors = statusColors(status);
  return (
    <View style={{ borderRadius: 999, borderWidth: 1, paddingHorizontal: 9, paddingVertical: 5, backgroundColor: colors.backgroundColor, borderColor: colors.borderColor, maxWidth: 120 }}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: colors.color, fontSize: 12, fontWeight: "900" }}>
        {verificationStatusLabel(status)}
      </Text>
    </View>
  );
}

function requestVerificationStatus(
  request: IdentityRequest | undefined,
  fallback: "unverified" | "pending" | "verified" | "rejected"
) {
  if (request?.status === "approved") return "verified" as const;
  if (request?.status === "pending") return "pending" as const;
  if (request?.status === "rejected") return "rejected" as const;
  return fallback;
}

function IdentityRequestCard({
  accountType,
  verificationStatus,
  loading,
  error,
  latestRequest,
  busy,
  note,
  declaredInstitution,
  detailsOpen,
  onChangeNote,
  onChangeInstitution,
  onToggleDetails,
  onSubmit,
  onCancel,
  onRetry,
}: {
  accountType: ReturnType<typeof getAccountType>;
  verificationStatus: ReturnType<typeof getVerificationStatus>;
  loading: boolean;
  error: boolean;
  latestRequest: Map<IdentityRequestKind, IdentityRequest>;
  busy: IdentityRequestKind | null;
  note: string;
  declaredInstitution: string;
  detailsOpen: boolean;
  onChangeNote: (value: string) => void;
  onChangeInstitution: (value: string) => void;
  onToggleDetails: () => void;
  onSubmit: (kind: IdentityRequestKind) => void;
  onCancel: (request: IdentityRequest) => void;
  onRetry: () => void;
}) {
  const teacherRequest = latestRequest.get("teacher_verification");
  const institutionRequest = latestRequest.get("institution_identity") ?? latestRequest.get("institution_conversion");
  const teacherStatus = requestVerificationStatus(teacherRequest, verificationStatus);
  const institutionStatus = institutionRequest ? requestVerificationStatus(institutionRequest, "unverified") : "unverified";
  const teacherApproved = teacherStatus === "verified";
  const teacherPending = teacherStatus === "pending";
  const institutionApproved = institutionStatus === "verified";
  const institutionPending = institutionStatus === "pending";

  return (
    <SettingsSection title="Vérification">
      {loading ? (
        <View style={{ minHeight: 56, alignItems: "center", justifyContent: "center", borderTopWidth: 1, borderTopColor: BORDER }}>
          <ActivityIndicator color={ACCENT} />
        </View>
      ) : error ? (
        <Pressable onPress={onRetry} accessibilityRole="button" style={{ minHeight: 56, justifyContent: "center", borderTopWidth: 1, borderTopColor: BORDER }}>
          <Text style={{ color: uiText.brand, fontWeight: "800" }}>Statut indisponible · Actualiser</Text>
        </Pressable>
      ) : (
        <>
          <View style={{ ...settingsRowStyle, minHeight: 64 }}>
            <Ionicons name="shield-checkmark-outline" size={19} color={uiText.secondary} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: MUTED, fontSize: 12, fontWeight: "800" }}>Statut</Text>
            </View>
            <StatusPill status={teacherStatus} />
          </View>

          {accountType === "teacher" && !teacherApproved ? (
            <View style={{ paddingVertical: 12, borderTopWidth: 1, borderTopColor: BORDER, gap: 8 }}>
              {detailsOpen ? (
                <BrandTextInput
                  value={note}
                  onChangeText={onChangeNote}
                  placeholder="Précision (facultatif)"
                  placeholderTextColor={uiText.muted}
                  multiline
                  style={requestInputStyle}
                />
              ) : null}
              <SmallAction
                label={teacherPending ? "Annuler la demande" : "Demander la vérification"}
                busy={busy === (teacherPending ? teacherRequest?.kind : "teacher_verification")}
                danger={teacherPending}
                onPress={() => {
                  if (teacherPending && teacherRequest) onCancel(teacherRequest);
                  else onSubmit("teacher_verification");
                }}
              />
              {!teacherPending ? <DetailsToggle open={detailsOpen} onPress={onToggleDetails} /> : null}
            </View>
          ) : null}

          {accountType !== "institution" ? (
            <View style={{ paddingVertical: 12, borderTopWidth: 1, borderTopColor: BORDER, gap: 8 }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>Identité école</Text>
                  <Text style={{ color: MUTED, marginTop: 2, fontSize: 12 }}>
                    {institutionRequest ? verificationStatusLabel(institutionStatus) : "Non demandée"}
                  </Text>
                </View>
              </View>
              {!institutionApproved && detailsOpen ? (
                <BrandTextInput
                  value={declaredInstitution}
                  onChangeText={onChangeInstitution}
                  placeholder="Nom de l’école (facultatif)"
                  placeholderTextColor={uiText.muted}
                  style={requestInputStyle}
                />
              ) : null}
              {!institutionApproved ? (
                <SmallAction
                  label={institutionPending ? "Annuler la demande" : "Demander une identité école"}
                  busy={busy === (institutionPending ? institutionRequest?.kind : "institution_identity")}
                  danger={institutionPending}
                  onPress={() => {
                    if (institutionPending && institutionRequest) onCancel(institutionRequest);
                    else onSubmit("institution_identity");
                  }}
                />
              ) : null}
              {!institutionPending && !institutionApproved ? <DetailsToggle open={detailsOpen} onPress={onToggleDetails} /> : null}
            </View>
          ) : null}
        </>
      )}
    </SettingsSection>
  );
}

const requestInputStyle = {
  backgroundColor: surface.overlay,
  borderColor: BORDER,
  borderRadius: 10,
  borderWidth: 1,
  color: uiText.primary,
  minHeight: 44,
  paddingHorizontal: 12,
  paddingVertical: 10,
};

function DetailsToggle({ open, onPress }: { open: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} accessibilityRole="button" style={{ alignSelf: "flex-start", paddingVertical: 2 }}>
      <Text style={{ color: uiText.brand, fontSize: 12, fontWeight: "800" }}>
        {open ? "Masquer la précision" : "Ajouter une précision (facultatif)"}
      </Text>
    </Pressable>
  );
}

function SmallAction({ label, busy, danger = false, onPress }: { label: string; busy: boolean; danger?: boolean; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      disabled={busy}
      onPress={onPress}
      style={({ pressed }) => ({
        alignSelf: "flex-start",
        minHeight: 42,
        justifyContent: "center",
        paddingHorizontal: 14,
        borderRadius: 10,
        borderColor: danger ? border.danger : ACCENT,
        borderWidth: 1,
        backgroundColor: danger ? surface.danger : ACCENT,
        opacity: busy ? 0.6 : pressed ? 0.78 : 1,
      })}
    >
      <Text style={{ color: uiText.primary, fontWeight: "900" }}>{busy ? "Traitement…" : label}</Text>
    </Pressable>
  );
}

function PasswordForm({
  currentPassword,
  newPassword,
  confirmPassword,
  savingPassword,
  sendingReset,
  onChangeCurrent,
  onChangeNew,
  onChangeConfirm,
  onChangePassword,
  onSendResetEmail,
}: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  savingPassword: boolean;
  sendingReset: boolean;
  onChangeCurrent: (value: string) => void;
  onChangeNew: (value: string) => void;
  onChangeConfirm: (value: string) => void;
  onChangePassword: () => void;
  onSendResetEmail: () => void;
}) {
  return (
    <View style={{ paddingVertical: 12, borderTopWidth: 1, borderTopColor: BORDER, gap: 8 }}>
      <BrandTextInput value={currentPassword} onChangeText={onChangeCurrent} secureTextEntry placeholder="Mot de passe actuel" style={passwordInputStyle} />
      <BrandTextInput value={newPassword} onChangeText={onChangeNew} secureTextEntry placeholder="Nouveau mot de passe" style={passwordInputStyle} />
      <BrandTextInput value={confirmPassword} onChangeText={onChangeConfirm} secureTextEntry placeholder="Confirmer le mot de passe" style={passwordInputStyle} />
      <Pressable onPress={onChangePassword} disabled={savingPassword} accessibilityRole="button" style={{ minHeight: 44, borderRadius: 10, backgroundColor: ACCENT, alignItems: "center", justifyContent: "center", opacity: savingPassword ? 0.7 : 1 }}>
        <Text style={{ color: uiText.primary, fontWeight: "900" }}>{savingPassword ? "Mise à jour…" : "Changer le mot de passe"}</Text>
      </Pressable>
      <Pressable onPress={onSendResetEmail} disabled={sendingReset} accessibilityRole="button" style={{ minHeight: 42, borderRadius: 10, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center", opacity: sendingReset ? 0.7 : 1 }}>
        <Text style={{ color: uiText.primary, fontWeight: "800" }}>{sendingReset ? "Envoi…" : "Réinitialiser par email"}</Text>
      </Pressable>
    </View>
  );
}

const passwordInputStyle = {
  color: uiText.primary,
  backgroundColor: surface.overlay,
  borderWidth: 1,
  borderColor: BORDER,
  borderRadius: 10,
  paddingHorizontal: 12,
  paddingVertical: 11,
};
